<!--
  Inline registration form for members: one section per guest, a stepper for
  the number of guests, validation on blur and the total on the button.
  Rendered inside the event ticket (first registration) and inside the
  registration card (adding guests to an existing registration).
-->
<script>
  import { getContext, tick } from 'svelte';
  import { nanoid } from 'nanoid';
  import { page } from '$app/state';
  import t, { LANG } from '#lib/i18n.svelte.js';
  import { format_price } from '#lib/util.js';
  import { submit_registration_changes, get_attendee_errors, translate_attendee_errors } from '#lib/event_registration_client.js';
  import TextInput from '#lib/TextInput.svelte';
  import ButtonLabel from '#lib/ButtonLabel.svelte';
  import EventChip from '#lib/EventChip.svelte';
  import '#lib/system-styles/event-ui.css';

  let {
    entry,
    capacity,
    /** Guests already registered by this member (caps the number of new rows) */
    existing_count = 0,
    /** The first row's e-mail is only required for a member's first registration */
    first_email_optional = false,
    /** Opened by a return from login or an e-mail link rather than a click:
        say what is left to do (the entry brings the ticket on screen) */
    returning = false,
    onsubmitted,
    oncancel,
  } = $props();

  const site_settings = getContext('site_settings');

  function create_row(prefill = {}) {
    return { temp_id: nanoid(), name: prefill.name ?? '', email: prefill.email ?? '', errors: {} };
  }

  // The member's own details prefill the first registration only
  // svelte-ignore state_referenced_locally
  let rows = $state([
    create_row(existing_count === 0 ? page.data.current_user ?? {} : {})
  ]);
  // The paid path leaves `submitting` true while navigating to Stripe Checkout.
  // A browser Back restoring this page from the bfcache is handled by the
  // entry: it refetches the registrations, and the member's new hold replaces
  // this form with their registration card.
  let submitting = $state(false);
  let submit_error = $state('');
  let form_ref = $state();
  let intro_ref = $state();
  // Paid events: an invoice only on request, since Stripe charges per invoice
  // and most members only need the receipt Stripe e-mails anyway
  let invoice_requested = $state(false);

  let max_rows = $derived(
    Math.max(1, Math.min((entry.max_guests_per_registration || 1) - existing_count, capacity.available))
  );

  // Free events: rows beyond the remaining seats land on the waitlist
  function is_waitlisted(index) {
    return !entry.payment_required && !capacity.is_unlimited && index >= capacity.remaining_seats;
  }
  let all_waitlisted = $derived(rows.length > 0 && is_waitlisted(0));

  let total = $derived(
    entry.payment_required
      ? format_price(entry.price_amount * rows.length, entry.currency_code, LANG)
      : ''
  );

  let submit_label = $derived(
    entry.payment_required ? t('submit_registration_paid')
    : all_waitlisted ? t('add_to_waitlist')
    : t('submit_registration')
  );

  /** "Du" for the member's own seat, "Gast 1", "Gast 2" … for everyone else, counted across the whole registration */
  function guest_heading(index) {
    return index === 0 && existing_count === 0 ? t('you') : t('guest_numbered', { number: existing_count + index });
  }

  function is_email_required(index) {
    return index === 0 && !first_email_optional;
  }

  function validate_row(row, index, { report_empty = true } = {}) {
    const errors = get_attendee_errors(row, { email_required: is_email_required(index), report_empty });
    row.errors = translate_attendee_errors(errors, t);
    return Object.keys(errors).length === 0;
  }

  function add_row() {
    if (rows.length < max_rows) rows.push(create_row());
  }

  function remove_last_row() {
    if (rows.length > 1) rows.pop();
  }

  async function submit(event) {
    event.preventDefault();
    rows.forEach((row, index) => validate_row(row, index));
    const invalid_row = rows.find(row => Object.keys(row.errors).length > 0);
    if (invalid_row) {
      await tick();
      const field = invalid_row.errors.name ? 'name' : 'email';
      document.getElementById(`guest_${field}_${invalid_row.temp_id}`)?.focus();
      return;
    }
    submitting = true;
    submit_error = '';
    const result = await submit_registration_changes(entry.entry_id, {
      added: rows.map(row => ({ name: row.name.trim(), email: row.email.trim() })),
      invoice_requested: Boolean(entry.payment_required) && invoice_requested,
    });
    if (result.checkout_url) {
      // Paid: the seats are held, Stripe Checkout completes them
      window.location.assign(result.checkout_url);
      return;
    }
    submitting = false;
    if (!result.success) {
      submit_error = t(result.error);
      return;
    }
    onsubmitted?.({ added: rows.length });
  }

  // Focus the first empty field once the form is on screen. On arrival the
  // member's details are usually prefilled; then the intro takes the focus,
  // so it is read out and the keyboard stands on nothing that submits. The
  // entry scrolls the ticket into view on arrival, so no focus scrolls then.
  $effect(() => {
    const first_empty = [...(form_ref?.querySelectorAll('input') ?? [])].find(input => !input.value);
    (first_empty ?? (returning ? intro_ref : null))?.focus({ preventScroll: returning });
  });
</script>

<form class='event-lines' onsubmit={submit} bind:this={form_ref} novalidate>

  {#if returning}
    <p class='text-control -semibold leading-trim event-arrival' tabindex='-1' bind:this={intro_ref}>{t('complete_registration_title')}</p>
    <p class='text-control text-2 leading-trim'>
      {t('complete_registration_note', { name: page.data.current_user?.name || page.data.current_user?.email })}
    </p>
  {/if}

  <div class='event-compact flex-column'>
    {#if max_rows > 1}
      <div class='event-guest-heading justify-between'>
        <span class='text-control event-label'>{t('people')}</span>
        <div class='event-stepper'>
          <button type='button' onclick={remove_last_row} disabled={rows.length <= 1 || submitting} aria-label={t('remove')}>
            <ButtonLabel variant='brand ghostly' start_icon='minus' />
          </button>
          <span class='event-stepper-value text-control'>{rows.length}</span>
          <button type='button' onclick={add_row} disabled={rows.length >= max_rows || submitting} aria-label={t('add_guest')}>
            <ButtonLabel variant='brand ghostly' start_icon='plus' />
          </button>
        </div>
      </div>
    {/if}

    {#each rows as row, index (row.temp_id)}
      <fieldset class='event-guest' disabled={submitting}>
        {#if rows.length > 1 || existing_count > 0}
          <div class='event-guest-heading'>
            <span class='text-control event-label'>{guest_heading(index)}</span>
            {#if is_waitlisted(index)}
              <EventChip>{t('waitlist')}</EventChip>
            {/if}
          </div>
        {/if}
        <TextInput
          id='guest_name_{row.temp_id}'
          class='event-field w-full'
          variant='brand ghostly'
          bind:value={row.name}
          placeholder={t('name')}
          aria_label={`${guest_heading(index)}: ${t('name')}`}
          autocomplete='name'
          required
          onblur={() => validate_row(row, index, { report_empty: false })}
          error={row.errors.name}
        />
        <TextInput
          id='guest_email_{row.temp_id}'
          class='event-field w-full'
          variant='brand ghostly'
          type='email'
          bind:value={row.email}
          placeholder={is_email_required(index) ? t('email') : t('email_optional')}
          aria_label={`${guest_heading(index)}: ${is_email_required(index) ? t('email') : t('email_optional')}`}
          autocomplete='email'
          inputmode='email'
          required={is_email_required(index)}
          onblur={() => validate_row(row, index, { report_empty: false })}
          error={row.errors.email}
        />
      </fieldset>
    {/each}
  </div>

  {#if entry.payment_required}
    <!-- Address and VAT id are asked for on the Checkout page itself -->
    <label class='event-compact event-check'>
      <input type='checkbox' bind:checked={invoice_requested} disabled={submitting} />
      <span class='text-control'>{t('need_invoice')}</span>
    </label>
  {/if}

  {#if submit_error}
    <p class='text-control text-error leading-trim'>{submit_error}</p>
  {/if}
  <button type='submit' class='full-width' disabled={submitting}>
    <ButtonLabel variant={`brand ${site_settings.primary_control_surface}`} class='full-width'>
      {#if submitting}
        {t('saving')}
      {:else}
        {submit_label}{#if entry.payment_required}<span class='event-aside'>{total}</span>{/if}
      {/if}
    </ButtonLabel>
  </button>
  {#if entry.payment_required}
    <p class='text-control text-2 leading-trim'>{t('payment_hold_note', { minutes: 30 })}</p>
  {/if}
  {#if oncancel}
    <div class='event-compact event-actions'>
      <button type='button' onclick={oncancel} disabled={submitting}>
        <ButtonLabel variant={`brand ${site_settings.secondary_control_surface}`}>{t('cancel')}</ButtonLabel>
      </button>
    </div>
  {/if}
</form>
